/**
 * WhatsApp Web pinned-window helper.
 *
 * Chrome 147+ severs window.opener once the popup navigates to web.whatsapp.com
 * (their COOP header is `same-origin-allow-popups`), which breaks the
 * `pinned.location` handle and even makes window.open(url, NAME) sometimes
 * spawn a new window. The reliable workaround is to navigate via an anchor
 * element with `target="tajWhatsApp"` — the browser uses its named-window
 * registry, which is preserved across COOP boundaries.
 *
 * Flow:
 *   - First click of header button → window.open with explicit size/position
 *     so the panel lands pinned to the right edge.
 *   - Every subsequent action → click a hidden <a target="tajWhatsApp">.
 *     Chrome navigates the existing named window. No new window.
 *   - If user closed the panel manually → fallback to window.open with
 *     features so the panel reappears.
 */
const WIN_NAME = 'tajWhatsApp';
const WEB_WA = 'https://web.whatsapp.com';
const WA_ME = 'https://wa.me';

const buildSendUrl = (waPhone, message) =>
  `${WEB_WA}/send?phone=${encodeURIComponent(waPhone)}&text=${encodeURIComponent(message || '')}`;

const popupFeatures = () => {
  const width = 420;
  const height = Math.min(820, window.screen.availHeight - 60);
  const left = Math.max(0, window.screen.availWidth - width - 8);
  const top = 40;
  return `popup=yes,width=${width},height=${height},left=${left},top=${top}`;
};

let pinned = null;
let lastOpenAt = 0;

const openPinned = (url) => {
  pinned = window.open(url, WIN_NAME, popupFeatures());
  if (pinned) lastOpenAt = Date.now();
  return pinned;
};

/** Click a hidden anchor with target=WIN_NAME to navigate the named window. */
const navigateViaAnchor = (url) => {
  const a = document.createElement('a');
  a.href = url;
  a.target = WIN_NAME;
  a.rel = 'noopener';        // we don't need the opener back
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
};

/** Open or focus the pinned WhatsApp Web window (header button). */
export const openWhatsAppPane = () => {
  if (pinned && !pinned.closed) {
    try { pinned.focus(); } catch {}
    return pinned;
  }
  return openPinned(WEB_WA);
};

/** Send a message to a specific number via the pinned pane (or fallback). */
export const sendToWhatsApp = (waPhone, message) => {
  if (!waPhone) return false;
  const url = buildSendUrl(waPhone, message);

  // If we have a live handle, reuse the panel via anchor click — Chrome 147+
  // handles target="tajWhatsApp" correctly while window.open often opens a
  // new window after WhatsApp Web's COOP severs the opener.
  if (pinned && !pinned.closed) {
    navigateViaAnchor(url);
    try { pinned.focus(); } catch {}
    return true;
  }

  // Pinned window was closed or never opened. If the user clicked very
  // recently (within a couple of seconds), still try the anchor — the
  // named window may exist in Chrome's registry even if our handle is null.
  if (Date.now() - lastOpenAt < 2000) {
    navigateViaAnchor(url);
    return true;
  }

  // Recreate the pinned panel directly at the send URL.
  if (openPinned(url)) return true;

  // Popup blocked entirely → last-resort fallback to wa.me in a tab.
  window.open(`${WA_ME}/${waPhone}?text=${encodeURIComponent(message || '')}`, '_blank', 'noopener,noreferrer');
  return false;
};

export const isPaneOpen = () => !!(pinned && !pinned.closed);
